import type { VariantProps } from "class-variance-authority";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * The raw `cva`. Never call this directly and never export it — its output is
 * unmerged, and the base string below carries six classes that a variant or a
 * size means to override but cannot: both land in the same `class` attribute at
 * equal specificity, so CSS source order decides, and in five of the six the
 * base is emitted last and wins. Measured in the compiled stylesheet, byte
 * offsets, on the build current at TRY-271:
 *
 * | base class                    | override                         | who wins |
 * | ----------------------------- | -------------------------------- | -------- |
 * | `border-transparent`  94519   | `border-border`  91649           | base ✗   |
 * | `rounded-lg`  88261           | `rounded-[min(…,10px\|12px)]`  87849/87951 | base ✗ |
 * | `[&_svg…]:size-4`  273089     | `…:size-3` / `…:size-3.5`  272760/272921   | base ✗ |
 * | `focus-visible:border-ring`  181945 | `…:border-destructive/40`  181646    | base ✗ |
 * | `focus-visible:ring-ring/50`  183311 | `…:ring-destructive/20`  183010     | base ✗ |
 * | `text-sm`  124267             | `text-xs` / `text-[0.8rem]`  124594/124711 | override ✓ |
 *
 * So a bare call rendered an invisible border in light theme (dark masked it,
 * `dark:border-input` outranking at 0,2,0), the wrong larger radius in *both*
 * themes, and 16px icons inside `xs`/`sm`/`icon-xs` buttons. The destructive
 * pair is real too but latent — no bare call site passes `destructive` today.
 *
 * `buttonVariants` below runs the output through `cn` (tailwind-merge), so every
 * one of these — and any future base-vs-variant conflict — resolves the way the
 * variant intended. See TRY-271.
 */
const buttonVariantsCva = cva(
	"group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
	{
		variants: {
			variant: {
				default: "bg-(--gold) text-(--gold-foreground) hover:bg-(--gold)/80",
				outline:
					"border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
				ghost:
					"hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
				destructive:
					"bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default:
					"h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2",
				xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-3.5",
				lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2",
				icon: "size-8",
				"icon-xs":
					"size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
				"icon-sm":
					"size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
				"icon-lg": "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

/**
 * Button chrome as a class string, for the cases that cannot be a `<Button>` —
 * a `<Link>`, a `<summary>`, a `<label>`. Merged, so a bare
 * `buttonVariants({ variant: "outline", size: "sm" })` renders exactly what
 * `<Button variant="outline" size="sm">` does.
 *
 * Merging here rather than at the 50 bare call sites is deliberate: `Button`
 * already wrapped every call in `cn`, so the majority path was merged all
 * along, and the five call sites that happened to be correct were correct only
 * because their authors needed an extra class and reached for `cn` anyway.
 *
 * ⚠️ Count them with the AST,
 * never with grep: `src/components/dashboard/page-header.tsx`'s docblock
 * contains the text `buttonVariants()` in prose, which inflates a grep by one,
 * and the split turns on whether the *nearest enclosing call* is `cn`.
 *
 * ⚠️ **So adding a `<Link className={buttonVariants(…)}>` anywhere in `src/`
 * now fails that gate until this docblock's digit is bumped**, and the second
 * figure is spelled as a word, so a sixth `cn`-wrapped site needs "six" here.
 * That obligation is recorded in CLAUDE.md §8 beside the ratchet table; bump
 * the number only after re-reading what the sentence claims, since bumping a
 * digit without reading it is the behaviour the gate exists to prevent.
 *
 * Verified across all 6 variants x 8 sizes: the merged string is byte-identical
 * to `cn(rawCva(…))`, which is exactly what `<Button>` was already rendering —
 * so `<Button>` itself cannot have moved — and every token twMerge drops has an
 * explicit replacement in the same group. Nothing is added.
 */
function buttonVariants(props?: Parameters<typeof buttonVariantsCva>[0]) {
	return cn(buttonVariantsCva(props));
}

function Button({
	className,
	variant = "default",
	size = "default",
	...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
	return (
		<ButtonPrimitive
			data-slot="button"
			className={buttonVariants({ variant, size, className })}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
